export type QuotationStatus = "Draft" | "Sent" | "Accepted" | "Rejected" | "Expired";

export interface QuotationLineItem {
  id: number;
  description: string;
  qty: number;
  unit: string;
  rate: number;
  taxPercent: number;
}

export interface Quotation {
  id: number;
  quoteNo: string;
  leadId: number;
  leadName: string;
  leadCompany: string;
  agent: string;
  date: string;
  validUntil: string;
  status: QuotationStatus;
  items: QuotationLineItem[];
  terms: string;
  discount: number;
}

export interface QuotationTemplate {
  id: number;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  itemCount: number;
  items: QuotationLineItem[];
}

export const statusBadgeColors: Record<QuotationStatus, string> = {
  Draft: "bg-gray-100 text-gray-700",
  Sent: "bg-blue-50 text-blue-700",
  Accepted: "bg-green-50 text-green-700",
  Rejected: "bg-red-50 text-red-600",
  Expired: "bg-orange-50 text-orange-700",
};

export const statusDotColors: Record<QuotationStatus, string> = {
  Draft: "bg-gray-400",
  Sent: "bg-blue-500",
  Accepted: "bg-green-500",
  Rejected: "bg-red-500",
  Expired: "bg-orange-500",
};

export const allQuotationStatuses: QuotationStatus[] = ["Draft", "Sent", "Accepted", "Rejected", "Expired"];

export const quotationsData: Quotation[] = [
  {
    id: 1, quoteNo: "QT-001", leadId: 1, leadName: "Vikram Mehta", leadCompany: "TechVentures Inc",
    agent: "Rajesh Kumar", date: "2026-06-20", validUntil: "2026-06-30", status: "Sent",
    discount: 0,
    items: [
      { id: 1, description: "2BHK Apartment — Andheri East", qty: 1, unit: "Unit", rate: 22000, taxPercent: 0 },
      { id: 2, description: "Parking Slot", qty: 1, unit: "Unit", rate: 1500, taxPercent: 18 },
      { id: 3, description: "Processing Fee", qty: 1, unit: "Lump Sum", rate: 1000, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 10 days\n2. Payment terms: 20% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability",
  },
  {
    id: 2, quoteNo: "QT-002", leadId: 2, leadName: "Anita Desai", leadCompany: "GreenLeaf Organics",
    agent: "Priya Sharma", date: "2026-06-15", validUntil: "2026-06-25", status: "Accepted",
    discount: 0,
    items: [
      { id: 1, description: "1BHK Apartment — Vashi", qty: 1, unit: "Unit", rate: 11500, taxPercent: 0 },
      { id: 2, description: "Registration Charges", qty: 1, unit: "Lump Sum", rate: 1300, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 30 days\n2. Payment terms: 10% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability",
  },
  {
    id: 3, quoteNo: "QT-003", leadId: 3, leadName: "Suresh Nair", leadCompany: "Skyline Architects",
    agent: "Rajesh Kumar", date: "2026-06-18", validUntil: "2026-07-15", status: "Draft",
    discount: 0,
    items: [
      { id: 1, description: "3BHK Penthouse — Bandra", qty: 1, unit: "Unit", rate: 38000, taxPercent: 0 },
      { id: 2, description: "Terrace Garden Setup", qty: 1, unit: "Lump Sum", rate: 2500, taxPercent: 18 },
      { id: 3, description: "Modular Kitchen", qty: 1, unit: "Unit", rate: 1800, taxPercent: 12 },
      { id: 4, description: "Interior Painting", qty: 1, unit: "Lump Sum", rate: 1200, taxPercent: 18 },
      { id: 5, description: "Club Membership", qty: 1, unit: "Unit", rate: 1700, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 30 days\n2. Payment terms: 15% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability",
  },
  {
    id: 4, quoteNo: "QT-004", leadId: 4, leadName: "Kavita Joshi", leadCompany: "BluePeak Media",
    agent: "Amit Patel", date: "2026-06-10", validUntil: "2026-06-20", status: "Accepted",
    discount: 0,
    items: [
      { id: 1, description: "Studio Apartment — Powai", qty: 1, unit: "Unit", rate: 8900, taxPercent: 0 },
    ],
    terms: "1. This quotation is valid for 15 days\n2. Payment terms: 25% on booking\n3. All prices inclusive of applicable taxes",
  },
  {
    id: 5, quoteNo: "QT-005", leadId: 8, leadName: "Sunita Rao", leadCompany: "PropMax Realty",
    agent: "Rajesh Kumar", date: "2026-06-22", validUntil: "2026-07-05", status: "Sent",
    discount: 2000,
    items: [
      { id: 1, description: "4BHK Villa — Lonavala", qty: 1, unit: "Unit", rate: 82000, taxPercent: 0 },
      { id: 2, description: "Swimming Pool Access", qty: 1, unit: "Unit", rate: 5000, taxPercent: 18 },
      { id: 3, description: "Landscaping", qty: 1, unit: "Lump Sum", rate: 3500, taxPercent: 12 },
      { id: 4, description: "Security System", qty: 1, unit: "Unit", rate: 4500, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 14 days\n2. Payment terms: 10% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability\n5. Construction timeline: 18 months",
  },
  {
    id: 6, quoteNo: "QT-006", leadId: 9, leadName: "Manoj Tiwari", leadCompany: "FastBuild Corp",
    agent: "Priya Sharma", date: "2026-06-08", validUntil: "2026-06-18", status: "Rejected",
    discount: 500,
    items: [
      { id: 1, description: "2BHK Apartment — Thane", qty: 1, unit: "Unit", rate: 45000, taxPercent: 0 },
      { id: 2, description: "Parking Slot", qty: 2, unit: "Unit", rate: 2000, taxPercent: 18 },
      { id: 3, description: "GST on Services", qty: 1, unit: "Lump Sum", rate: 3000, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 10 days\n2. Payment terms: 20% on booking\n3. All prices inclusive of applicable taxes",
  },
  {
    id: 7, quoteNo: "QT-007", leadId: 5, leadName: "Rahul Verma", leadCompany: "NovaTech Labs",
    agent: "Sneha Rao", date: "2026-06-19", validUntil: "2026-07-10", status: "Draft",
    discount: 0,
    items: [
      { id: 1, description: "1BHK Apartment — Panvel", qty: 1, unit: "Unit", rate: 28000, taxPercent: 0 },
      { id: 2, description: "Maintenance Deposit", qty: 1, unit: "Lump Sum", rate: 3000, taxPercent: 0 },
    ],
    terms: "1. This quotation is valid for 21 days\n2. Payment terms: 15% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability",
  },
  {
    id: 8, quoteNo: "QT-008", leadId: 6, leadName: "Deepika Singh", leadCompany: "EduSmart",
    agent: "Priya Sharma", date: "2026-06-21", validUntil: "2026-06-28", status: "Sent",
    discount: 0,
    items: [
      { id: 1, description: "2BHK Apartment — Kharghar", qty: 1, unit: "Unit", rate: 15500, taxPercent: 0 },
      { id: 2, description: "Parking Slot", qty: 1, unit: "Unit", rate: 1500, taxPercent: 18 },
      { id: 3, description: "Registration Charges", qty: 1, unit: "Lump Sum", rate: 1500, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 7 days\n2. Payment terms: 10% on booking\n3. All prices inclusive of applicable taxes",
  },
  {
    id: 9, quoteNo: "QT-009", leadId: 16, leadName: "Ravi Iyer", leadCompany: "CloudFirst Solutions",
    agent: "Priya Sharma", date: "2026-06-12", validUntil: "2026-06-22", status: "Accepted",
    discount: 1000,
    items: [
      { id: 1, description: "3BHK Apartment — Seawoods", qty: 1, unit: "Unit", rate: 58000, taxPercent: 0 },
      { id: 2, description: "Parking Slot", qty: 2, unit: "Unit", rate: 2000, taxPercent: 18 },
      { id: 3, description: "Modular Kitchen", qty: 1, unit: "Unit", rate: 2500, taxPercent: 12 },
      { id: 4, description: "Club Membership", qty: 1, unit: "Unit", rate: 1800, taxPercent: 18 },
      { id: 5, description: "Power Backup", qty: 1, unit: "Unit", rate: 1200, taxPercent: 5 },
      { id: 6, description: "Documentation Fee", qty: 1, unit: "Lump Sum", rate: 2500, taxPercent: 18 },
    ],
    terms: "1. This quotation is valid for 15 days\n2. Payment terms: 10% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability",
  },
  {
    id: 10, quoteNo: "QT-010", leadId: 10, leadName: "Pooja Menon", leadCompany: "StyleHub",
    agent: "Sneha Rao", date: "2026-05-28", validUntil: "2026-06-10", status: "Expired",
    discount: 0,
    items: [
      { id: 1, description: "1BHK Apartment — Ulwe", qty: 1, unit: "Unit", rate: 5500, taxPercent: 0 },
    ],
    terms: "1. This quotation is valid for 14 days\n2. Payment terms: 20% on booking\n3. All prices inclusive of applicable taxes",
  },
];

export function computeLineAmount(item: QuotationLineItem): number {
  return item.qty * item.rate * (1 + item.taxPercent / 100);
}

export function computeSubtotal(items: QuotationLineItem[]): number {
  return items.reduce((sum, item) => sum + item.qty * item.rate, 0);
}

export function computeTaxTotal(items: QuotationLineItem[]): number {
  return items.reduce((sum, item) => sum + item.qty * item.rate * (item.taxPercent / 100), 0);
}

export function computeTotal(items: QuotationLineItem[], discount: number): number {
  return computeSubtotal(items) + computeTaxTotal(items) - discount;
}

export const defaultLineItems: QuotationLineItem[] = [
  { id: 1, description: "2BHK Flat — Bandra West", qty: 1, unit: "Unit", rate: 22000000, taxPercent: 0 },
  { id: 2, description: "Registration & Stamp Duty", qty: 1, unit: "Lump Sum", rate: 150000, taxPercent: 18 },
  { id: 3, description: "Brokerage Fee", qty: 1, unit: "Lump Sum", rate: 245000, taxPercent: 18 },
];

export const defaultTerms = "1. This quotation is valid for 30 days\n2. Payment terms: 10% on booking\n3. All prices inclusive of applicable taxes\n4. Subject to availability";

export const companyInfo = {
  name: "PropMax Solutions",
  address: "123 Business Park, Mumbai",
  phone: "+91-22-12345678",
  email: "sales@propmax.com",
  gstin: "27AAAAA0000A1Z5",
};

export const taxOptions = [0, 5, 12, 18, 28];

export const quotationTemplates: QuotationTemplate[] = [
  {
    id: 1,
    name: "2BHK Standard Package",
    description: "Standard 2BHK flat with registration and legal charges",
    createdBy: "Rajesh Kumar",
    createdAt: "2026-05-15",
    itemCount: 4,
    items: [
      { id: 1, description: "2BHK Flat", qty: 1, unit: "Unit", rate: 22000000, taxPercent: 0 },
      { id: 2, description: "Registration & Stamp Duty", qty: 1, unit: "Lump Sum", rate: 150000, taxPercent: 18 },
      { id: 3, description: "Brokerage Fee", qty: 1, unit: "Lump Sum", rate: 245000, taxPercent: 18 },
      { id: 4, description: "Legal Charges", qty: 1, unit: "Lump Sum", rate: 35000, taxPercent: 18 },
    ],
  },
  {
    id: 2,
    name: "3BHK Premium Package",
    description: "Premium 3BHK flat with interior consultation and full legal support",
    createdBy: "Priya Sharma",
    createdAt: "2026-04-20",
    itemCount: 5,
    items: [
      { id: 1, description: "3BHK Flat", qty: 1, unit: "Unit", rate: 38000000, taxPercent: 0 },
      { id: 2, description: "Registration & Stamp Duty", qty: 1, unit: "Lump Sum", rate: 250000, taxPercent: 18 },
      { id: 3, description: "Brokerage Fee", qty: 1, unit: "Lump Sum", rate: 450000, taxPercent: 18 },
      { id: 4, description: "Interior Consultation", qty: 1, unit: "Lump Sum", rate: 500000, taxPercent: 18 },
      { id: 5, description: "Legal Charges", qty: 1, unit: "Lump Sum", rate: 75000, taxPercent: 18 },
    ],
  },
  {
    id: 3,
    name: "Plot + Construction",
    description: "Residential plot purchase with construction and approval charges",
    createdBy: "Rajesh Kumar",
    createdAt: "2026-03-10",
    itemCount: 4,
    items: [
      { id: 1, description: "Plot Cost", qty: 1, unit: "Unit", rate: 25000000, taxPercent: 0 },
      { id: 2, description: "Construction Cost", qty: 1, unit: "Lump Sum", rate: 8000000, taxPercent: 5 },
      { id: 3, description: "Architect Fee", qty: 1, unit: "Lump Sum", rate: 500000, taxPercent: 18 },
      { id: 4, description: "Approval Charges", qty: 1, unit: "Lump Sum", rate: 200000, taxPercent: 18 },
    ],
  },
  {
    id: 4,
    name: "Commercial Space",
    description: "Commercial unit with CAM charges and complete registration",
    createdBy: "Amit Patel",
    createdAt: "2026-02-22",
    itemCount: 4,
    items: [
      { id: 1, description: "Commercial Unit", qty: 1, unit: "Unit", rate: 60000000, taxPercent: 0 },
      { id: 2, description: "CAM Charges (1 Year)", qty: 1, unit: "Lump Sum", rate: 2500000, taxPercent: 18 },
      { id: 3, description: "Registration & Stamp Duty", qty: 1, unit: "Lump Sum", rate: 500000, taxPercent: 18 },
      { id: 4, description: "Legal Documentation", qty: 1, unit: "Lump Sum", rate: 150000, taxPercent: 18 },
    ],
  },
  {
    id: 5,
    name: "Affordable Housing",
    description: "Budget-friendly 1BHK flat with minimal charges",
    createdBy: "Sneha Rao",
    createdAt: "2026-01-08",
    itemCount: 2,
    items: [
      { id: 1, description: "1BHK Flat", qty: 1, unit: "Unit", rate: 12000000, taxPercent: 0 },
      { id: 2, description: "Registration Charges", qty: 1, unit: "Lump Sum", rate: 120000, taxPercent: 18 },
    ],
  },
];